import { ActionEventArgs, InPlaceEditor } from '@syncfusion/ej2-inplace-editor';

import { routes } from '@/shared/routes';
import { EditableClientProps, EditableOption } from '@/shared/types/editable';
import { isEmptyArrayOrNull, isEmptyStringOrNull } from '@/shared/utils/misc';
import { offcanvasBackUrl } from '@/shared/utils/url';

import { datePickerModel } from '@/front/editable/date-picker';
import { dropDownlistModel } from '@/front/editable/drop-down-list';
import { multiSelectModel } from '@/front/editable/multi-select';
import { richTextEditorModel } from '@/front/editable/rich-text-editor';
import { setEditor } from '@/front/editable/state';
import { textBoxModel, textEditorModel } from '@/front/editable/text-box';
import { GET } from '@/front/fetch';

export const initInPlaceEditor = async (p: EditableClientProps, e: HTMLElement) => {
  if (p.type !== 'Switch') {
    const cm = createControlModel(p);

    const editor = new InPlaceEditor({
      locale: cm.locale,
      mode: 'Inline',
      name: p.field,
      primaryKey: p.modelId,
      type: p.type === 'Integer' || p.type === 'Float' ? 'Text' : p.type,
      value: cm.value,
      model: cm,
      emptyText: p.emptyText ?? 'Not set',
      validationRules: p.validation ? { [p.field]: p.validation } : undefined,
      disabled: p.readOnly,
      actionOnBlur: 'Cancel',
      url: p.url ?? routes.data.editable({ model: p.modelName }),
      adaptor: 'UrlAdaptor',
      actionSuccess: (args: ActionEventArgs) => {
        e.setAttribute(
          'data-empty',
          isEmptyStringOrNull(args.value) || isEmptyArrayOrNull(args.value) ? 'true' : 'false',
        );
      },
      ...createEditorModel(p),
    });

    setEditor(p.id, editor);

    if (isEmptyStringOrNull(cm.value) || isEmptyArrayOrNull(cm.value)) {
      e.setAttribute('data-empty', 'true');
    }

    e.setAttribute('data-underline', 'false');

    editor.appendTo(e);

    const editableValue = e.getElementsByClassName('e-editable-value')[0] as HTMLSpanElement;

    if (typeof cm.value === 'string' || typeof cm.value === 'number') {
      if (p.lookup) {
        const url = `${routes.data.lookup.item({ model: p.lookup, value: cm.value })}?includeArchived=true`;

        const res = await GET(url);

        const item = (await res.json()) as EditableOption;

        editableValue.innerHTML = item.text;
      } else if (p.options) {
        const text = Object.values(p.options).find((option) => option.value === cm.value)?.text;

        if (text) {
          editableValue.innerHTML = text;
        }
      }
    }

    if (p.urlTemplate || p.obscured || p.copyable || p.validation?.email) {
      const span = document.createElement('span');

      if (p.urlTemplate) {
        span.appendChild(
          createIconButton({
            onClick: () => {
              const url = p.urlTemplate!.replaceAll('${value}', editor.value.toString());

              if (url.startsWith('http')) {
                window.open(url);
              } else {
                const offcanvas = new URLSearchParams(location.search).get('offcanvas') === 'true';

                location.href = offcanvas ? offcanvasBackUrl(url) : url;
              }
            },
            className: 'fa-solid fa-arrow-up-right-from-square',
          }),
        );
      }

      if (p.obscured) {
        span.appendChild(
          createIconButton({
            onClick: () => {
              e.setAttribute('data-obscured', e.dataset.obscured === 'true' ? 'false' : 'true');
            },
            className: 'fa-solid fa-eye',
          }),
        );
      }

      if (p.copyable) {
        span.appendChild(
          createIconButton({
            onClick: () => {
              navigator.clipboard.writeText(editableValue.textContent ?? '');
            },
            className: 'fa-solid fa-paste',
          }),
        );
      }

      if (p.validation?.email) {
        span.appendChild(
          createIconButton({
            url: `mailto:${cm.value}`,
            className: 'fa-solid fa-envelope',
          }),
        );
      }

      e.after(span);
    }
  }
};

const createControlModel = (p: EditableClientProps) => {
  const { type } = p;

  if (type === 'Text' || type === 'Float' || type === 'Integer') {
    return textBoxModel(p);
  } else if (type === 'Date') {
    return datePickerModel(p);
  } else if (type === 'DropDownList') {
    return dropDownlistModel(p);
  } else if (type === 'MultiSelect') {
    return multiSelectModel(p);
  } else if (type === 'RTE') {
    return richTextEditorModel(p);
  }

  throw new Error('Invalid type');
};

const createEditorModel = (p: EditableClientProps) => {
  if (p.type === 'Text') {
    return textEditorModel(p);
  }
};

const createIconButton = (params: { url?: string; newWindow?: boolean; onClick?: () => void; className: string }) => {
  const { url, newWindow, onClick, className } = params;

  const a = document.createElement('a');

  a.href = url ?? '#';
  a.className = 'mx-1';

  if (newWindow) {
    a.target = '_blank';
  }

  if (onClick) {
    a.addEventListener('click', (event) => {
      event.preventDefault();
      onClick();
    });
  }

  const i = document.createElement('i');

  i.className = className;

  a.appendChild(i);

  return a;
};
